import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Target, Mail, LineChart, ShieldCheck, Sparkles, Send, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const features = [
  { icon: Target, title: "Pinpoint fit", body: "Describe what you sell. We surface companies most likely to buy — ranked 1-10." },
  { icon: Mail, title: "Drafts ready", body: "Every lead comes with a tailored cold email and LinkedIn note — opt-out line baked in." },
  { icon: LineChart, title: "Track pipeline", body: "Move leads through new → contacted → replied → meeting booked, all in one view." },
  { icon: ShieldCheck, title: "Truthful by design", body: "We never fabricate contacts. Every lead has a working source URL you can verify." },
];

export default function Landing() {
  const [contactEmail, setContactEmail] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/config/public").then(({ data }) => setContactEmail(data.contact_email || "")).catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "", website: "" });
      toast.success("Message sent — we'll get back to you shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden spark-glow">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20 md:pt-32 md:pb-28">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs tracking-[0.18em] uppercase mb-8">
                <Sparkles className="w-3 h-3" /> AI B2B lead research
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.95]">
                Find your next <span className="text-primary">100 customers</span><br />
                before coffee gets cold.
              </h1>
              <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed font-light">
                Client Scout reads your business and generates a vetted list of companies most likely to buy —
                with decision-maker roles, fit scores, and drafted outreach ready to copy.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button asChild size="lg" className="rounded-full bg-primary hover:bg-[#FF7043] text-white px-7 h-12" data-testid="hero-cta-signup">
                  <Link to="/register">Start free — 10 leads <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-border" data-testid="hero-cta-login">
                  <Link to="/login">I have an account</Link>
                </Button>
              </div>
              <p className="mt-6 text-xs text-muted-foreground">AI-generated; verify before contacting.</p>
            </div>

            <div className="md:col-span-4 relative">
              <div className="tracing-border rounded-2xl bg-[#121214] p-6 shadow-2xl">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Sample lead</div>
                <div className="text-2xl font-medium tracking-tight">Northstar Logistics</div>
                <div className="text-sm text-muted-foreground mt-1">Toronto, CA · 51-200</div>
                <div className="mt-5 flex items-center gap-2">
                  <div className="text-xs text-muted-foreground">Fit score</div>
                  <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: "92%" }} />
                  </div>
                  <div className="text-primary mono text-sm">9.2</div>
                </div>
                <div className="mt-5 text-sm leading-relaxed text-muted-foreground">
                  Expanding mid-market logistics firm actively hiring in ops — strong fit for route-optimization SaaS.
                </div>
                <div className="mt-5 flex gap-2">
                  <div className="text-xs px-2 py-1 rounded border border-border text-muted-foreground">VP Operations</div>
                  <div className="text-xs px-2 py-1 rounded border border-primary/30 bg-primary/10 text-primary">New</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <div className="overline text-primary">What you get</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-medium tracking-tight leading-tight">
              Research hours, compressed into one request.
            </h2>
            <p className="mt-5 text-muted-foreground font-light leading-relaxed">
              Describe your business once. Choose a target market and list size. We return a ranked list of
              real companies, who to reach, why they fit, and what to say.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-[#121214] p-6 hover:-translate-y-0.5 transition-transform duration-200 ease-out">
                <f.icon className="w-5 h-5 text-primary" />
                <div className="mt-4 text-lg font-medium tracking-tight">{f.title}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="tracing-border rounded-2xl bg-[#121214] p-10 md:p-14 relative overflow-hidden">
          <div className="grain absolute inset-0 opacity-40" />
          <div className="relative">
            <h3 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight max-w-2xl">
              Your first 10 leads are on us.
            </h3>
            <p className="mt-4 text-muted-foreground max-w-xl font-light">
              Free account, no card required. Takes under a minute.
            </p>
            <div className="mt-8">
              <Button asChild size="lg" className="rounded-full bg-primary hover:bg-[#FF7043] text-white px-8 h-12" data-testid="footer-cta-signup">
                <Link to="/register">Create free account <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-6xl mx-auto px-6 pb-24 scroll-mt-24" data-testid="contact-section">
        <div className="grid md:grid-cols-5 gap-10 md:gap-14 items-start">
          <div className="md:col-span-2">
            <div className="overline text-primary">Contact</div>
            <h3 className="mt-3 text-3xl md:text-4xl font-medium tracking-tight leading-tight">
              Talk to a human.
            </h3>
            <p className="mt-4 text-muted-foreground font-light leading-relaxed">
              Have a question about pricing, deliverability, or whether Client Scout fits
              your niche? Drop us a line and we'll reply within one business day.
            </p>
            <div className="mt-8 space-y-4 text-sm">
              {contactEmail ? (
                <a
                  href={`mailto:${contactEmail}`}
                  className="flex items-center gap-3 group"
                  data-testid="contact-admin-email"
                >
                  <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Email</div>
                    <div className="font-mono text-foreground group-hover:text-primary transition-colors">{contactEmail}</div>
                  </div>
                </a>
              ) : (
                <div className="flex items-center gap-3 opacity-50" data-testid="contact-admin-email-loading">
                  <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm text-muted-foreground">Loading…</div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Response time</div>
                  <div className="text-foreground">Within 1 business day</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            {sent ? (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-8 md:p-10 text-center" data-testid="contact-sent-state">
                <div className="w-12 h-12 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto">
                  <Send className="w-5 h-5 text-primary" />
                </div>
                <h4 className="mt-4 text-xl font-medium tracking-tight">Message received</h4>
                <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
                  Thanks for reaching out. We'll reply to your email within one business day.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-6 rounded-full"
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <form
                onSubmit={submitContact}
                className="rounded-2xl border border-border bg-[#121214] p-8 md:p-10 space-y-5"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-name">Your name</Label>
                    <Input
                      id="contact-name"
                      required
                      maxLength={120}
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Jane Doe"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="jane@acme.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="Tell us what you're building and how we can help…"
                    data-testid="contact-message"
                  />
                  <div className="text-xs text-muted-foreground text-right mono">
                    {form.message.length} / 4000
                  </div>
                </div>
                {/* Honeypot — hidden from humans, bots will fill it and we silently drop. */}
                <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                    />
                  </label>
                </div>
                <div className="flex items-center justify-end pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="rounded-full bg-primary hover:bg-[#FF7043] text-white px-6 h-11"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Sending…" : (<>Send message <Send className="w-4 h-4 ml-2" /></>)}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 text-xs text-muted-foreground flex justify-between items-center gap-4 flex-wrap">
          <div>© {new Date().getFullYear()} Client Scout</div>
          <div className="flex items-center gap-4">
            <a href="/#contact" className="hover:text-foreground transition-colors" data-testid="footer-contact-link">Contact</a>
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} className="hover:text-primary transition-colors mono" data-testid="footer-admin-email">
                {contactEmail}
              </a>
            )}
            <div>AI-generated; verify before contacting.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
